import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import { ReactElement, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from 'components/v2/Table'
import { KreditActions } from 'features/Kredits/views/Index/Actions'
import { IKreditsListProps } from 'features/Kredits/views/Index/List/types'
import { KreditsMultipleActions } from 'features/Kredits/views/Index/MultipleActions'
import { Checkbox } from 'components/v2/Checkbox'
import { GTKredit } from 'gql/types.generated'
import { GTKreditAccountType } from 'gql/types.generated'
import { intToLetter } from 'utils/number'
import { isNumber } from 'lodash'

export const KreditsList = ({ kredits = [] }: IKreditsListProps): ReactElement => {
  const canSeeMultipleActions = true //TODO: https://kolors.atlassian.net/browse/NA-14421
  const [selectedItems, setSelectedItems] = useState<GTKredit[]>([])

  const handleSelectAll = (): void => {
    if (selectedItems.length === kredits.length) {
      setSelectedItems([])
    } else {
      setSelectedItems(kredits)
    }
  }

  const handleSelect = (kredit: GTKredit): void => {
    if (selectedItems.map(({ id }) => id).includes(kredit.id)) {
      setSelectedItems(selectedItems.filter(({ id }) => id !== kredit.id))
    } else {
      setSelectedItems([...selectedItems, kredit])
    }
  }

  return (
    <TableContainer overflow>
      <Table marginBottom>
        <Thead>
          <Tr>
            {canSeeMultipleActions && (
              <>
                <Th noUppercase>
                  <KreditsMultipleActions selectedItems={selectedItems} />
                </Th>
                <Th>
                  <Checkbox
                    checked={selectedItems.length === kredits.length}
                    onChange={(): void => {
                      handleSelectAll()
                    }}
                  />
                </Th>
              </>
            )}
            <Th>
              <FormattedMessage id="kredits.list.header.user.name" defaultMessage="User name" />
            </Th>
            <Th>
              <FormattedMessage id="kredits.list.header.user.mail" defaultMessage="User mail" />
            </Th>
            <Th>
              <FormattedMessage id="kredits.list.header.user.phone" defaultMessage="User phone" />
            </Th>
            <Th>
              <FormattedMessage id="kredits.list.header.balance" defaultMessage="Balance" />
            </Th>
            <Th>
              <FormattedMessage id="kredits.list.header.client" defaultMessage="Client" />
            </Th>
            <Th>
              <FormattedMessage id="kredits.list.header.site" defaultMessage="Site" />
            </Th>
            <Th>
              <FormattedMessage id="kredits.list.header.category" defaultMessage="Category" />
            </Th>
            <Th>
              <FormattedMessage id="kredits.list.header.refund" defaultMessage="Refund" />
            </Th>
            <Th>
              <FormattedMessage id="kredits.list.header.corporate" defaultMessage="Corporate" />
            </Th>
            <Th>
              <FormattedMessage id="kredits.list.header.recharge" defaultMessage="Recharge" />
            </Th>
            <Th>
              <FormattedMessage id="kredits.list.header.bonus" defaultMessage="Bonus" />
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {kredits.map((kredit): ReactElement => {
            const refundsBalance = kredit.accounts.find(
              (account) => account.type === GTKreditAccountType.Refund
            )?.balance
            const corporateBalance = kredit.accounts.find(
              (account) => account.type === GTKreditAccountType.Corporate
            )?.balance
            const rechargeBalance = kredit.accounts.find(
              (account) => account.type === GTKreditAccountType.TopUp
            )?.balance
            const bonusBalance = kredit.accounts.find((account) => account.type === GTKreditAccountType.Bonus)?.balance
            return (
              <Tr key={kredit.id}>
                <Td>
                  <KreditActions kredit={kredit} isDisabled={selectedItems.map(({ id }) => id).includes(kredit.id)} />
                </Td>
                {canSeeMultipleActions && (
                  <Td>
                    <Checkbox
                      id={kredit.id}
                      checked={selectedItems.map(({ id }) => id).includes(kredit.id)}
                      onChange={(): void => {
                        handleSelect(kredit)
                      }}
                    />
                  </Td>
                )}
                <Td>{kredit.user?.userFullName}</Td>
                <Td>{kredit.user?.email}</Td>
                <Td>{kredit.user?.phone}</Td>
                <Td>
                  <strong>
                    <FormattedPrice value={kredit.balance} />
                  </strong>
                </Td>
                <Td>{kredit.businessClient?.name ?? '-'} </Td>
                <Td>{kredit.businessSite?.name ?? '-'}</Td>
                <Td>
                  {isNumber(kredit.businessCustomer?.category)
                    ? intToLetter(Number(kredit.businessCustomer?.category) as number)
                    : '-'}
                </Td>
                <Td>{refundsBalance ? <FormattedPrice value={Math.abs(refundsBalance)} /> : '-'}</Td>
                <Td>{corporateBalance ? <FormattedPrice value={Math.abs(corporateBalance)} /> : '-'}</Td>
                <Td>{rechargeBalance ? <FormattedPrice value={Math.abs(rechargeBalance)} /> : '-'}</Td>
                <Td>{bonusBalance ? <FormattedPrice value={Math.abs(bonusBalance)} /> : '-'}</Td>
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
